"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrackedEvent = exports.eventId = exports.eventIdMatcher = void 0;
const ids_1 = require("./ids");
const localEvent_1 = require("./localEvent");
const objects_1 = require("./objects");
const logging_1 = require("./logging");
/** RegEx to test for an event ID value. */
exports.eventIdMatcher = /^evt_[a-zA-Z0-9]{16}$/;
/** Create a new event ID using our `evt_[SHORT_ID]` format. See `eventIdMatcher` for expected format. */
const eventId = () => (0, ids_1.shortId)('evt');
exports.eventId = eventId;
/**
 * Use to automatically add/maintain TrackedEventInfo to event info when notifying/bubbling.
 *
 * Supports bubbling via `bubbleTo` method.
 */
class TrackedEvent extends localEvent_1.LocalEvent {
    /**
     * Create a new tracked event.
     *
     * @param eventName - the name of the event.  Used for logging and uniquely identifying the event.
     * @param options - options to control the behavior of the event.
     */
    constructor(eventName, options = {}) {
        super(eventName);
        this.options = options;
        this.bubbleHandlers = [];
        /**
         * Subscribe to this event to add behavior when new subscriptions are added.
         **/
        this.onSubscribe = new localEvent_1.LocalEvent(`${this.eventName}/onSubscribe`);
        if (options.sendLatestOnSubscribe) {
            this.onSubscribe.subscribe((handler) => {
                if (this.lastEventInfo) {
                    handler(this.cloneTrackedEventInfo(this.lastEventInfo));
                }
            });
        }
    }
    /** Adds any missing required TrackedEventInfo data (and ensures we have an object to report it). */
    ensureEventTracking(eventInfo) {
        var _a;
        // we use Partial to allow the eventId to be optional in the TSourceInfo type
        const trackedInfo = eventInfo !== null && eventInfo !== void 0 ? eventInfo : {};
        // we want to keep prior event ID if it's there (most likely from bubbling)
        if (!exports.eventIdMatcher.test((_a = trackedInfo.eventId) !== null && _a !== void 0 ? _a : '')) {
            trackedInfo.eventId = (0, exports.eventId)();
        }
        if (!(Number(trackedInfo.eventTimestamp) > 0)) {
            trackedInfo.eventTimestamp = Date.now();
        }
        return trackedInfo;
    }
    /** If cloning is not disabled, will clone and keep originEvent reference. */
    cloneTrackedEventInfo(eventInfo) {
        if (this.options.disableCloning) {
            return eventInfo;
        }
        // we want to maintain reference to originEvent
        const originEvent = eventInfo.originEvent;
        delete eventInfo.originEvent; // remove so we don't waste time cloning
        const cloned = (0, objects_1.deepClone)(eventInfo);
        if (originEvent !== undefined) {
            cloned.originEvent = originEvent;
            // also reset on given in case something has a reference to it
            eventInfo.originEvent = originEvent;
        }
        return cloned;
    }
    /**
     * Notify subscribers with the given event info.
     *
     * Will ensure requried TrackedEventInfo is added if not already specified (such as from bubbling).
     *
     * @param eventInfo - the event info to notify with.
     */
    notify(eventInfo) {
        // handle the case where the TSourceInfo is undefined because we need an object to provide the eventId ond originEvent
        const trackedEventInfo = this.ensureEventTracking(eventInfo);
        this.lastEventInfo = trackedEventInfo;
        // we don't use super here because we want to clone for each handler in order to isolate them from each other
        // to prevent data modfications from one handler from affecting another
        this.handlers.forEach((handler) => {
            try {
                handler(this.cloneTrackedEventInfo(trackedEventInfo));
            }
            catch (ex) {
                (0, logging_1.warn)(`Problem notifying event '${this.eventName}' subscriber.`, trackedEventInfo, ex);
            }
        });
    }
    /**
     *
     * Subscribe to this event.
     *
     * Will ensure requried TrackedEventInfo is added if not already specified (such as from bubbling).
     * Subscribers will receive a clone of the event info to prevent data modfications from one handler from affecting another.
     *
     * @param handler - the function to call when the event occurs.
     */
    subscribe(handler) {
        if (!this.handlers.some((h) => h === handler)) {
            this.onSubscribe.notify(handler);
        }
        super.subscribe(handler);
    }
    /**
     * Bubbles this event to the given target event.
     *
     * Appends the originEvent to the event info, which will either be this event or a more fine-grained event that bubbled to this event.
     * Also ensures required TrackedEventInfo data is bubbled, even if mapper omits it.
     *
     * @param targetEvent - the event to bubble to
     * @param mapper - a function that maps the lower level event info to the target event info. Mapper can omit TrackedEventInfo data as it will be included automatically.
     */
    bubbleTo(targetEvent, mapper) {
        if (this.bubbleHandlers.find((bh) => bh.targetEvent.eventName === targetEvent.eventName)) {
            (0, logging_1.warn)(`Already bubbling to event '${targetEvent.eventName}' from '${this.eventName}'.`);
            return;
        }
        const bubbleHandler = (sourceInfo) => {
            var _a;
            // conditionally set the originEvent with this event, if it's not already set by a more fine-grained event
            // we don't trust the mapper to pass the originEvent through, so we check and set it ourselves
            const targetInfo = Object.assign(mapper(sourceInfo), {
                originEvent: (_a = sourceInfo.originEvent) !== null && _a !== void 0 ? _a : this,
                // ensure we're bubbling tracked event data
                eventId: sourceInfo.eventId,
                eventTimestamp: sourceInfo.eventTimestamp,
            });
            targetEvent.notify(targetInfo);
        };
        this.subscribe(bubbleHandler);
        // keep these so we can resub if we clear all
        this.bubbleHandlers.push({ targetEvent, handler: bubbleHandler });
    }
    /**
     * Stop bubbling to event previously added via `bubbleTo`.
     *
     * @param targetEvent - the event to stop bubbling to. Will remove based on matching event name.
     */
    stopBubblingTo(targetEvent) {
        const bubbleHandler = this.bubbleHandlers.find((bh) => bh.targetEvent.eventName === targetEvent.eventName);
        if (bubbleHandler) {
            this.unsubscribe(bubbleHandler.handler);
            this.bubbleHandlers = this.bubbleHandlers.filter((bh) => bh.targetEvent.eventName !== targetEvent.eventName);
        }
    }
    /**
     * Clears all subscribers when enabled,
     * except for those that were added implicitly via `bubbleTo` so that bubbling can continue to work.
     *
     * @throws if `allowClearing` is false.
     * @see TrackedEventOptions in constructor.
     */
    clearAll() {
        if (this.options.allowClearing) {
            super.clearAll();
            // resubscribe to bubble handlers
            this.bubbleHandlers.forEach((bh) => this.subscribe(bh.handler));
        }
        else {
            throw new Error(`Cannot clear subscriptions for event '${this.eventName}' because allowClearing is false.`);
        }
    }
}
exports.TrackedEvent = TrackedEvent;
